import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { BaseConfigurationService } from 'app/services/base-configuration.service';
import { EnterpriseService } from 'app/services/enterprise.service';
import { UserService } from 'app/services/user.service';
import { Observable } from 'rxjs';

const DEFAULT_TIMEZONE = 'Europe/Madrid';

const resolveTimezone = (enterpriseService: EnterpriseService): string => {
  const selectedTimezone = enterpriseService.selectedEnterprise?.timezone;
  if (selectedTimezone) {
    return selectedTimezone;
  }

  try {
    const enterpriseRaw = localStorage.getItem('enterprise');
    if (enterpriseRaw) {
      const parsed = JSON.parse(enterpriseRaw);
      return parsed?.timezone || DEFAULT_TIMEZONE;
    }
  } catch {
    return DEFAULT_TIMEZONE;
  }

  return DEFAULT_TIMEZONE;
};

export function tokenInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const backendEndpoint = inject(BaseConfigurationService).getBackendEndpoint();

  /*
   * Solo se saltan las cabeceras cuando la URL apunta **fuera de nuestra API**: aparte de
   * no filtrar el token a otro servicio, cualquier cabecera que el destino no declare en
   * `Access-Control-Allow-Headers` tumba el preflight, que es lo que pasaba con el motor
   * de turnos, que no conoce `timezone`.
   *
   * Ojo con comprobar solo si es absoluta. Este interceptor corre **después** de
   * `backEndpointInterceptor` (ver el orden en `app.config.ts`), que ya ha prefijado la
   * URL con `backEndpoint` —que es absoluto en todos los entornos menos el vacío—, así
   * que a estas alturas *todas* las llamadas a nuestra API son absolutas. Descartarlas
   * por eso dejaba a la API entera sin cabeceras, y el fallo no se veía de golpe: los
   * endpoints que llevan la empresa en la query seguían respondiendo, y solo caían los
   * que dependen de `selectedenterpriseid`, con un 404 «Enterprise not found».
   */
  const isAbsolute = /^https?:\/\//i.test(req.url);
  const isOurBackend = !!backendEndpoint && req.url.startsWith(backendEndpoint);
  if (isAbsolute && !isOurBackend) {
    return next(req);
  }
  if (req.url.includes('i18n/') || req.url.includes('icons/') || req.url.includes('api/')) {
    return next(req);
  }

  const authService = inject(AuthService);
  const userService = inject(UserService);
  const enterpriseService = inject(EnterpriseService);
  const dupReq = req.clone({
    setHeaders: {
      token: authService.accessToken || '',
      loggeduserid: userService.getUser()?.id || '',
      selectedenterpriseid: enterpriseService.selectedEnterprise?.id || '',
      timezone: resolveTimezone(enterpriseService),
    },
  });
  return next(dupReq);
}
